import type { Localidad } from './localidades.service';

/**
 * Sugerencias de provincia para el autocompletado del buscador.
 *
 * El autocompletado de `GET /localidades?q=` sólo devuelve localidades,
 * barrios y estaciones (Google acepta como máximo cinco tipos de lugar y el
 * cupo está lleno), así que las provincias se suman acá, desde esta lista
 * fija: son 24 y no cambian. Elegir una agrega la zona "Provincia de X" (o
 * "Ciudad Autónoma de Buenos Aires"), que el backend filtra por provincia
 * exacta (`provinciaDeZona`) y el mapa contornea entera (`zona-partido.ts`).
 *
 * "Córdoba" a secas sigue siendo la ciudad: por eso la provincia se ofrece
 * siempre con el "Provincia de" adelante.
 */

/** Provincias del catálogo del backend (`provincias.catalog.ts`), sin CABA. */
const PROVINCIAS: ReadonlyArray<{ slug: string; nombre: string }> = [
  { slug: 'buenos-aires', nombre: 'Buenos Aires' },
  { slug: 'catamarca', nombre: 'Catamarca' },
  { slug: 'chaco', nombre: 'Chaco' },
  { slug: 'chubut', nombre: 'Chubut' },
  { slug: 'cordoba', nombre: 'Córdoba' },
  { slug: 'corrientes', nombre: 'Corrientes' },
  { slug: 'entre-rios', nombre: 'Entre Ríos' },
  { slug: 'formosa', nombre: 'Formosa' },
  { slug: 'jujuy', nombre: 'Jujuy' },
  { slug: 'la-pampa', nombre: 'La Pampa' },
  { slug: 'la-rioja', nombre: 'La Rioja' },
  { slug: 'mendoza', nombre: 'Mendoza' },
  { slug: 'misiones', nombre: 'Misiones' },
  { slug: 'neuquen', nombre: 'Neuquén' },
  { slug: 'rio-negro', nombre: 'Río Negro' },
  { slug: 'salta', nombre: 'Salta' },
  { slug: 'san-juan', nombre: 'San Juan' },
  { slug: 'san-luis', nombre: 'San Luis' },
  { slug: 'santa-cruz', nombre: 'Santa Cruz' },
  { slug: 'santa-fe', nombre: 'Santa Fe' },
  { slug: 'santiago-del-estero', nombre: 'Santiago del Estero' },
  { slug: 'tierra-del-fuego', nombre: 'Tierra del Fuego' },
  { slug: 'tucuman', nombre: 'Tucumán' },
];

const NOMBRE_CABA = 'Ciudad Autónoma de Buenos Aires';

/** Formas de escribir CABA que la sugieren, además de su nombre. */
const ALIAS_CABA = ['caba', 'capital federal'];

/** Cuántas provincias se sugieren como máximo, antes de las de Google. */
const MAX_PROVINCIAS = 2;

/** Lo tipeado tiene que tener al menos esto para sugerir provincias. */
const MIN_CARACTERES = 2;

/** Prefijos con los que el usuario puede escribir la provincia. */
const PREFIJOS = ['provincia de ', 'pcia. de ', 'pcia de '];

/** Minúsculas, sin acentos y con un solo espacio entre palabras. */
function normalizar(texto: string): string {
  return Array.from(texto.normalize('NFD'))
    .filter((c) => {
      const codigo = c.codePointAt(0) ?? 0;
      return codigo < 0x300 || codigo > 0x36f;
    })
    .join('')
    .toLowerCase()
    .split(' ')
    .filter(Boolean)
    .join(' ');
}

/** El nombre empieza con lo tipeado, o alguna de sus palabras. */
function coincide(nombre: string, tipeado: string): boolean {
  const normalizado = normalizar(nombre);
  return (
    normalizado.startsWith(tipeado) ||
    normalizado.split(' ').some((palabra) => palabra.startsWith(tipeado))
  );
}

/**
 * Provincias que coinciden con lo tipeado, como sugerencias del
 * autocompletado ("cord" → "Provincia de Córdoba"; "caba" o "buenos" →
 * "Ciudad Autónoma de Buenos Aires").
 */
export function sugerirProvincias(q: string): Localidad[] {
  let tipeado = normalizar(q);
  const prefijo = PREFIJOS.find((p) => tipeado.startsWith(p));
  if (prefijo) tipeado = tipeado.slice(prefijo.length);
  if (tipeado.length < MIN_CARACTERES) return [];

  const sugerencias: Localidad[] = [];
  if (
    coincide(NOMBRE_CABA, tipeado) ||
    ALIAS_CABA.some((alias) => alias.startsWith(tipeado))
  ) {
    sugerencias.push({
      value: 'provincia-caba',
      label: NOMBRE_CABA,
      nombre: NOMBRE_CABA,
      detalle: 'Ciudad',
      provincia: 'caba',
    });
  }
  for (const { slug, nombre } of PROVINCIAS) {
    if (!coincide(nombre, tipeado)) continue;
    const zona = `Provincia de ${nombre}`;
    sugerencias.push({
      value: `provincia-${slug}`,
      label: zona,
      nombre: zona,
      detalle: 'Provincia',
      provincia: slug,
    });
  }
  return sugerencias.slice(0, MAX_PROVINCIAS);
}
